import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Storefront, Eye, EyeSlash, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LanguageToggle } from "@/i18n/I18nContext";

const BG = "https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("owner@bellaos.com");
  const [password, setPassword] = useState("bella123");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate(res.user?.role === "superadmin" ? "/admin" : "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-[#FDFBF9]">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Salon interior" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#1E2B25]/55" />
        <div className="relative h-full flex flex-col justify-between p-12 text-[#FDFBF9]">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center">
              <Storefront size={20} weight="duotone" />
            </div>
            <span className="font-display font-extrabold text-xl tracking-tight">BellaOS</span>
          </Link>
          <div>
            <h1 className="font-display text-4xl sm:text-5xl font-light leading-tight tracking-tight">{t.auth.heroTitle}</h1>
            <p className="mt-5 text-white/75 text-base max-w-md leading-relaxed">{t.auth.heroSub}</p>
          </div>
          <div className="text-xs tracking-[0.2em] uppercase text-white/50">{t.auth.heroTags}</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 fade-up">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="lg:hidden flex items-center gap-2.5">
              <div className="h-9 w-9 rounded-xl bg-[#2C3E35] flex items-center justify-center"><Storefront size={20} weight="duotone" color="#FDFBF9" /></div>
              <span className="font-display font-extrabold text-xl">BellaOS</span>
            </Link>
            <div className="ml-auto"><LanguageToggle /></div>
          </div>
          <p className="label-caps">{t.auth.welcome}</p>
          <h2 className="font-heading text-3xl font-medium tracking-tight mt-2 mb-8">{t.auth.signinTitle}</h2>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="label-caps block mb-2">{t.auth.email}</label>
              <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition" placeholder="you@studio.com" />
            </div>
            <div>
              <label className="label-caps block mb-2">{t.auth.password}</label>
              <div className="relative">
                <input data-testid="login-password" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#E5E2DC] outline-none focus:ring-1 focus:ring-[#2C3E35] focus:border-[#2C3E35] transition pr-11" placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A67]">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && <div data-testid="login-error" className="text-sm text-[#B86B5A] bg-[#B86B5A]/8 rounded-lg px-4 py-2.5">{error}</div>}

            <button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full py-3 rounded-lg bg-[#2C3E35] text-white font-medium hover:bg-[#1E2B25] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? t.auth.signingIn : t.auth.signin} {!loading && <ArrowRight size={18} />}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#6B6A67]">
            {t.auth.noAccount}{" "}
            <Link to="/register" data-testid="goto-register" className="text-[#2C3E35] font-semibold hover:text-[#B86B5A] transition-colors">{t.auth.createStudio}</Link>
          </p>
          <div className="mt-8 text-xs text-[#6B6A67] bg-[#E8E4DF]/50 rounded-lg px-4 py-3">{t.auth.demoHint}</div>
        </div>
      </div>
    </div>
  );
}
